/*  Private Device Vault — WebAuthn biometric wrapper (namespaced, iter238).
    Enroll wraps a JSON payload of per-namespace key bytes (Health + Items).
    Unlock returns an object { [ns]: Uint8Array }. Backwards compatible with
    the legacy single-key wrap: if an older device payload decrypts to raw
    bytes rather than JSON, it is returned under the "health" namespace so the
    caller can detect the mismatch and prompt re-enrollment.                  */

import { metaGet, metaSet, metaDelete } from "./storage";

const META_BIO_CRED_ID = "biometric_cred_id";
const META_BIO_WRAPPED = "biometric_wrapped_key";
const RP_NAME = "TravelerPulse Private Vault";

function _u8ToB64Url(u8) {
  let s = ""; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function _b64UrlToU8(str) {
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  const bin = atob(str);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

export async function biometricAvailable() {
  if (!window.PublicKeyCredential) return false;
  try { return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); }
  catch { return false; }
}

async function _deviceKeyFromCred(rawIdU8) {
  const material = await crypto.subtle.importKey("raw", rawIdU8, { name: "HKDF" }, false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0),
      info: new TextEncoder().encode("tp-vault-biometric-wrap") },
    material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"],
  );
}

/** Enroll biometric. `keyBytesMap` is `{ health: Uint8Array, items: Uint8Array }`. */
export async function enrollBiometric(userId, keyBytesMap) {
  if (!(await biometricAvailable())) throw new Error("Biometric authenticator unavailable on this device");
  if (!keyBytesMap || !keyBytesMap.health || !keyBytesMap.items)
    throw new Error("Biometric enrollment requires both namespace keys");

  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userIdBytes = new TextEncoder().encode(userId || "vault-user");
  const cred = await navigator.credentials.create({
    publicKey: {
      rp: { name: RP_NAME, id: window.location.hostname },
      user: { id: userIdBytes, name: "vault-user", displayName: "Private Vault" },
      challenge,
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required", residentKey: "preferred" },
      timeout: 60_000, attestation: "none",
    },
  });
  if (!cred || !cred.rawId) throw new Error("Credential creation cancelled");

  const rawIdU8   = new Uint8Array(cred.rawId);
  const deviceKey = await _deviceKeyFromCred(rawIdU8);
  const iv        = crypto.getRandomValues(new Uint8Array(12));
  const payload   = new TextEncoder().encode(JSON.stringify({
    v: 2,
    keys: {
      health: _u8ToB64Url(keyBytesMap.health),
      items:  _u8ToB64Url(keyBytesMap.items),
    },
  }));
  const wrappedBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, deviceKey, payload);

  await metaSet(META_BIO_CRED_ID, _u8ToB64Url(rawIdU8));
  await metaSet(META_BIO_WRAPPED, {
    wrappedKey: _u8ToB64Url(new Uint8Array(wrappedBuf)),
    iv: _u8ToB64Url(iv),
  });
  return true;
}

/** Prompt biometric → returns `{ health: Uint8Array, items: Uint8Array }`. */
export async function unlockWithBiometric() {
  const credIdB64 = await metaGet(META_BIO_CRED_ID);
  const wrap      = await metaGet(META_BIO_WRAPPED);
  if (!credIdB64 || !wrap) throw new Error("Biometric not enrolled on this device");

  const credId    = _b64UrlToU8(credIdB64);
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge, timeout: 60_000, userVerification: "required",
      allowCredentials: [{ type: "public-key", id: credId }],
      rpId: window.location.hostname,
    },
  });
  if (!assertion) throw new Error("Biometric prompt cancelled");

  const rawIdU8   = new Uint8Array(assertion.rawId);
  const deviceKey = await _deviceKeyFromCred(rawIdU8);
  const iv        = _b64UrlToU8(wrap.iv);
  const wrappedU8 = _b64UrlToU8(wrap.wrappedKey);
  const plainBuf  = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, deviceKey, wrappedU8);
  const plainU8   = new Uint8Array(plainBuf);

  // v2 payload = JSON `{v:2, keys:{health, items}}`. Detect by leading `{`.
  if (plainU8.length && plainU8[0] === 0x7b /* "{" */) {
    try {
      const parsed = JSON.parse(new TextDecoder().decode(plainU8));
      if (parsed?.v === 2 && parsed.keys?.health && parsed.keys?.items) {
        return {
          health: _b64UrlToU8(parsed.keys.health),
          items:  _b64UrlToU8(parsed.keys.items),
        };
      }
    } catch { /* fall through to legacy */ }
  }
  // Legacy v1: raw 32-byte key. Return under health; caller detects missing items and re-enrolls.
  return { health: plainU8, items: null };
}

export async function hasBiometricEnrolled() {
  const id = await metaGet(META_BIO_CRED_ID);
  return !!id;
}

export async function removeBiometric() {
  await metaDelete(META_BIO_CRED_ID);
  await metaDelete(META_BIO_WRAPPED);
}
